import { IAirspaceBoundaryProperties, IAirspaceProperties, IMilitaryTrainingRouteProperties, INSUFRProperties, INationalParkProperties, IRecreationalFixedFlyerSiteProperties, ITFRProperties, IWildernessAreaUSFSProperties, IWildernessAreaFWSProperties, IWildernessAreaBLMProperties, IWildlifeRefugeProperties, ICDFWPublicLandsProperties, MissionFeatureCollection, INearbyAdvisoryProperties, ISpecialUseAdvisoryProperties, IAirportProperties, IAdvisoryPropertiesV3, IFRIAProperties, ISEAMSProperties } from '@autopylot-internal/tiles-client';
import { SecondaryAdvisory, SecondaryWarningAdvisory } from '../SecondaryAdvisory.tsx';
import { PendingAdvisory } from '../PendingAdvisory.tsx';
import { AdvisoryCompleteIcon } from '../../components/icons/Icons.tsx';
import mapboxgl from 'mapbox-gl';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { DevSettings } from '../../support/DevSettings.ts';
import { DirectionIndicator } from '../../weather/components/DirectionIndicator.tsx';
import { Logger } from '../../support/debug/Logger.ts';
import { MapViewModel } from '../../map/model/MapViewModel.ts';
import { airspaceBoundaryNameOverride } from './AirspaceBoundaryAdvisory.tsx';
import { getName as getSpecialUseAirspaceName } from './SpecialUseAdvisory.tsx';
import { AdvisoryProvider } from '../AdvisoryProvider.ts';
import { MilitaryTrainingRoutesAdvisory } from './MilitaryTrainingRoutesAdvisory.tsx';

const getAirportLabel = (a: IAirportProperties) => {
    const airportType = ({
        'HP': 'Heliport',
        'AD': 'Airport',
        'SP': 'Seaport',
        'UL': 'Ultra Light',
        'GL': 'Glider Port',
        'BP': 'Balloon Port'
    })[a.TYPE_CODE];
    const prefix = airportType ? `${airportType}: ` : '';
    const suffix =  a.TYPE_CODE === 'AD' && Boolean(a.IDENT) ? ` ${a.IDENT}` : '';
    return `${prefix}${a.NAME}${suffix}`;
};

// TODO: ideally, we'd get all these types from @autopylot-internal/tiles
const getAdvisoryLabel = (properties: IAdvisoryPropertiesV3) => {
    const sourceToRenderer: {[source: string]: (a: any) => string} = {
        'military-training-routes': (a: IMilitaryTrainingRouteProperties) => `Military Training Route ${a.IDENT}`,
        'nsufr': (a: INSUFRProperties) => `NSUFR ${a.Base || a.Facility}`,
        'parttime-nsufr': (a: INSUFRProperties) => `NSUFR ${a.Base || a.Facility}`,
        'temporary-flight-restrictions': (a: ITFRProperties) => `TFR ${a.number}`,
        'seams': (a: ISEAMSProperties) => a.VENUE,
        'airspace-boundary': (a: IAirspaceBoundaryProperties) => `${airspaceBoundaryNameOverride[a.GLOBAL_ID] || a.NAME}`,
        'uasfm+authorizable': (uasfm: any) => `LAANC ${uasfm.airspaceIdent} Class ${uasfm.airspaceClasses.join(', ')}`,
        'airspace': (a: IAirspaceProperties) => a.NAME,
        'special-use-airspace': (a: ISpecialUseAdvisoryProperties) => getSpecialUseAirspaceName(a),
        'national-parks': (a: INationalParkProperties) => a.UNIT_NAME,
        'wilderness-areas-usfs': (a: IWildernessAreaUSFSProperties) => a.WILDERNESSNAME,
        'wilderness-areas-fws': (a: IWildernessAreaFWSProperties) => a.DESNAME,
        'wilderness-areas-blm': (a: IWildernessAreaBLMProperties) => a.NLCS_NAME,
        'wildlife-refuges': (a: IWildlifeRefugeProperties) => a.ORGNAME,
        'cdfw-public-lands': (a: ICDFWPublicLandsProperties) => a.PROP_NAME,
        'fria': (a: IFRIAProperties) => `${a.title} FRIA`,
        'rec-flyer-fixed-sites': (a: IRecreationalFixedFlyerSiteProperties) => a.SITE_NAME,
        'airports': (a: IAirportProperties) => getAirportLabel(a)
    };
    const renderer = sourceToRenderer[properties.source];
    return renderer?.(properties);
};

const Miles: React.FC<{ meters: number; }> = ({ meters }) => {
    const miles = meters/1609;

    const formattedMiles = miles.toFixed(1);
    return (
        <span style={{ marginLeft: 10 }}>{formattedMiles}mi</span>
    );
};

const Direction: React.FC<{ direction: number; show?: boolean; }> = ({ direction, show }) => {
    return (
        <span style={{ visibility: show ? 'visible' : 'hidden', marginLeft: 10 }}>
            <DirectionIndicator direction={direction} />
        </span>
    );
};

let activeMarker: { marker: mapboxgl.Marker; advisoryGlobalId?: string; }  | undefined = undefined;

class AccidentalUnsupportedAdvisoryLogger {
    static inject = () => [Logger];
    constructor(private logger: Logger){}

    private alreadyLogged: string[] = [];
    log = (...sources: string[]) => {
        for (const source of sources){
            if (!this.alreadyLogged.includes(source)){
                this.alreadyLogged.push(source);
                this.logger.error(`WARN: OperationalAwarenessAdvisory - has no renderer for source ${source}. Either add it, or add to a whitelist.`);
            }
        }
    };
}

export const OperationalAwarenessAdvisory = (props: INearbyAdvisoryProperties & { mission: MissionFeatureCollection } ) => {
    const accidentalUnsupportedAdvisoryLogger = useInstance(AccidentalUnsupportedAdvisoryLogger);
    const devSettings = useInstance(DevSettings);
    const mapViewModel = useInstance(MapViewModel);
    const advisoryProvider = useInstance(AdvisoryProvider);

    if (!props.advisories) {
        return (
            <PendingAdvisory header="Operational Awareness">
                Checking for potential hazards near your flight plan...
            </PendingAdvisory>
        );
    }

    const advisories = (props.advisories)
        .filter(a =>  {
            // these sources are no longer returned, but they might exist on old missions,
            // so ignore them so they don't trip AccidentalUnsupportedAdvisoryLogger
            if (['scheduled-airspace', 'airspace-info', 'stadiums', 'wilderness-areas'].includes(a.properties.source)) {
                return false;
            }

            // show all the ones more 0m away, b/c it can't be redundant with the mission
            // (we normalize anything within the mission polygon to 0 distanceMeters)
            if (a.properties.distanceMeters > 0) {
                return true;
            }

            const isMissionBlocked = props.mission.canFly() === 'cannot-fly';
            if (isMissionBlocked){
                // if blocked, then already shown
                let isSecondaryAdvisoryShown = a.properties.advisoryType === 'block';

                // if advise we show when blocked, then already shown
                isSecondaryAdvisoryShown = isSecondaryAdvisoryShown || advisoryProvider.getAdviseSourcesToShowWhenBlocked().includes(a.properties.source);

                // filter to only those that we aren't already showing, eg DC SFUR
                return !isSecondaryAdvisoryShown;
            }

            // then not blocked
            // most advisories are shown when not blocked, so filter to only those that are never shown as Secondary Advisory
            const advisoriesNeverSecondary = ['airports', 'seams'];
            return advisoriesNeverSecondary.includes(a.properties.source);
        })
        .sort((a, b) => a.properties.distanceMeters - b.properties.distanceMeters)
        .map(a => {
            // add label to advisory properties
            const label = getAdvisoryLabel(a.properties);
            return { ...a, properties: { ...a.properties, label } };
        })
        .filter((a, i, arr) => {
            // only show first (nearest) advisory for each label
            if (!a.properties.label) {
                accidentalUnsupportedAdvisoryLogger.log(a.properties.source);
                return false;
            }
            return i === arr.findIndex(_a => _a.properties.label === a.properties.label);
        });

    if (advisories.length === 0){
        return (
            <SecondaryAdvisory header="Operational Awareness" advisoryType="advise" icon={AdvisoryCompleteIcon} iconColor="success">
                No potential hazards identified near your flight plan.
            </SecondaryAdvisory>
        );
    }

    const nearbyMTRs = advisories.filter(a => a.properties.source === 'military-training-routes');
    const compositeMTRAdvisories = advisoryProvider.buildCompositeMTRAdvisoryArray(nearbyMTRs.map(a => a.properties));

    const s = advisories.length === 1 ? '' : 's';
    const are = advisories.length === 1 ? 'is' : 'are';
    const them = advisories.length ===1 ? 'it' : 'them';

    return (
        <>
            {compositeMTRAdvisories.map((a, i) => <MilitaryTrainingRoutesAdvisory near key={i} {...a} />)}

            <SecondaryWarningAdvisory header="Operational Awareness" advisoryType={props.advisoryType}>
                <>
                    There {are} {advisories.length} potential hazard{s} within about 2 miles of your flight plan. Please review {them} before operation.
                    {advisories.map((a, i: number) => {

                        const addMarker = () => {
                            if (!devSettings.showNearestOperationalAwarenessPosition){
                                return;
                            }
                            const isMarkerAlreadyShowingAdvisoryById = activeMarker && activeMarker.advisoryGlobalId === a.properties.GLOBAL_ID;
                            const isMarkerAtAdvisoryPosition = activeMarker && activeMarker.marker.getLngLat().lng === a.geometry.coordinates[0] && activeMarker.marker.getLngLat().lat === a.geometry.coordinates[1];

                            // cannot guarantee all advisories have a GLOBAL_ID, so use position as a backup
                            // but don't only use position that way two advisories with the same position, don't act like they are the same advisory
                            const isAdvisoryAlreadyShowingPosition = activeMarker && activeMarker.advisoryGlobalId ? isMarkerAlreadyShowingAdvisoryById : isMarkerAtAdvisoryPosition;

                            // then just remove it (toggling it off)
                            if (activeMarker && isAdvisoryAlreadyShowingPosition){
                                activeMarker.marker.remove();
                                activeMarker = undefined;
                                return;
                            }

                            if (activeMarker){
                                activeMarker.marker.remove();
                                activeMarker = undefined;
                            }

                            const [lng, lat] = a.geometry.coordinates;

                            activeMarker = {
                                advisoryGlobalId: a.properties.GLOBAL_ID,
                                marker: new mapboxgl.Marker()
                                    .setLngLat({ lng, lat })
                                    // this is an internal tool, so not worth adding the complexity into MapViewModel
                                    .addTo((mapViewModel as any).map)
                            };
                        };

                        return (
                            <div key={i} onClick={addMarker} style={{
                                marginTop: 4,
                                display: 'flex',

                                // use the margin on the right that is usually for the secondary advisory icon
                                // this assumes there was enough content above, so we're below the icon vertically, not stacked under neath it
                                marginRight: -60
                            }}>
                                <div style={{
                                    flexGrow: 2,
                                }}>
                                    {a.properties.label}
                                </div>
                                <div style={{ width: 80, flexShrink: 0 }}>
                                    <Miles meters={a.properties.distanceMeters} />
                                    <Direction direction={a.properties.direction} show={a.properties.distanceMeters > 0} />
                                </div>
                            </div>
                        );
                    })}
                </>
            </SecondaryWarningAdvisory>
        </>
    );
};
